import { useImperativeHandle, useMemo, useRef, useState } from 'react';
import { type AnyFieldApi, useForm, useSelector } from '@tanstack/react-form';

import { useAuth } from '@nvision/identity-core';
import { DatePickerField, InputField, SelectField } from '@nvision/ui';

import {
  BranchField,
  CarrierField,
  CarrierServiceField,
  CountryField,
  CurrencyField,
  DirectInterlineField,
  EquipmentTypeField,
  type LaneRow,
  LaneSearchModal,
  type LocationRow,
  LocationSearchModal,
  type PortRow,
  type PortSearchFormValues,
  PortSearchModal,
  type PostalSearchFormValues,
  PostalSearchModal,
  type Postals,
  PricingTypeField,
  ShipTypeField,
  StateField,
} from '@/modules/entities';
import { getEffectiveDateBounds } from '@/utils/date';

import { useAvailableAccessorialCodes } from '../../../hooks';
import { type AccessorialsFilters, accessorialsFiltersSchema, DANGEROUS_GOODS_OPTIONS } from '../../../models';

import styles from './accessorials-search-filters-form.module.css';

export type AccessorialsSearchFiltersFormRef = {
  submit: () => void;
  reset: (value: AccessorialsFilters) => void;
  canSubmit: () => boolean;
};

interface AccessorialsSearchFiltersProps {
  customerKey?: number;
  formRef: React.RefObject<AccessorialsSearchFiltersFormRef>;
  onSubmit: (values: AccessorialsFilters, meta: { customerKey: number }) => void;
  initialValues: AccessorialsFilters;
}

type LocationFieldName = 'originLocationKey' | 'destinationLocationKey';
type PortFieldName = 'originPortKey' | 'destinationPortKey';
type PostalFieldName = 'originPostalFrom' | 'originPostalTo' | 'destinationPostalFrom' | 'destinationPostalTo';

export const AccessorialsSearchFiltersForm: React.FC<AccessorialsSearchFiltersProps> = ({
  customerKey,
  formRef,
  onSubmit,
  initialValues,
}) => {
  const [activeLocationField, setActiveLocationField] = useState<LocationFieldName | null>(null);
  const [activePortField, setActivePortField] = useState<PortFieldName | null>(null);
  const [activePostalField, setActivePostalField] = useState<PostalFieldName | null>(null);
  const [isLanesModalOpened, setIsLanesModalOpened] = useState(false);
  const [portDefaultValues, setPortDefaultValues] = useState<PortSearchFormValues>({} as PortSearchFormValues);
  const [postalDefaultValues, setPostalDefaultValues] = useState<PostalSearchFormValues>({} as PostalSearchFormValues);
  const {
    data: accessorialCodes,
    isLoading: accessorialCodesLoading,
    updateFilters: updateAccessorialCodeFilters,
  } = useAvailableAccessorialCodes();

  const defaultValuesRef = useRef(initialValues);
  const form = useForm({
    defaultValues: defaultValuesRef.current,
    onSubmit: async (props) => {
      if (props.value && customerKey) {
        onSubmit(props.value, { customerKey });
      }
    },
    validators: {
      onChange: accessorialsFiltersSchema,
    },
  });

  const resetForm = (values: AccessorialsFilters) => {
    defaultValuesRef.current = values;
    form.reset(values, { keepDefaultValues: false });
  };

  const values = useSelector(form.store, (state) => state.values);
  const auth = useAuth();
  const timeZone = auth.user?.profile.pref.timezone;
  const originCountryCode = values.originCountry ? String(values.originCountry.id) : undefined;
  const destinationCountryCode = values.destinationCountry ? String(values.destinationCountry.id) : undefined;
  const { effectiveFromMaxValue: rangeFromMaxValue, effectiveToMinValue: rangeToMinValue } = useMemo(
    () => getEffectiveDateBounds(values.startEffectiveFromDate, values.endEffectiveToDate, timeZone),
    [timeZone, values.startEffectiveFromDate, values.endEffectiveToDate],
  );
  const { effectiveFromMaxValue, effectiveToMinValue } = useMemo(
    () => getEffectiveDateBounds(values.effectiveFromDate, values.effectiveToDate, timeZone),
    [timeZone, values.effectiveFromDate, values.effectiveToDate],
  );

  const getError = (field: AnyFieldApi) =>
    field.state.meta.isTouched
      ? field.state.meta.errors
          .map((e: { message?: string }) => e?.message)
          .filter(Boolean)
          .join('. ')
      : undefined;

  useImperativeHandle(formRef, () => ({
    canSubmit: () => form.state.canSubmit,
    reset: resetForm,
    submit: () => {
      form.validate('submit');
      form.handleSubmit();
    },
  }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  const handleLocationSelectionChange = (location: LocationRow) => {
    if (!activeLocationField) {
      return;
    }

    form.setFieldValue(activeLocationField, {
      id: location.locationKey,
      label: location.locationCode ?? String(location.locationKey),
    });
    setActiveLocationField(null);
  };

  const getPostalDefaultValues = (field: PostalFieldName): PostalSearchFormValues => {
    const isOrigin = field.startsWith('origin');
    const country = isOrigin ? values.originCountry : values.destinationCountry;
    const state = isOrigin ? values.originState : values.destinationState;

    return {
      additional: {
        city: (isOrigin ? values.originCity : values.destinationCity) ?? undefined,
        portPostal: values[field] ?? undefined,
        state: state ? { id: String(state.id), label: state.label } : undefined,
      },
      country: {
        id: country ? String(country.id) : '',
        label: country?.label ?? '',
      },
    };
  };

  const openPostalSearchModal = (field: PostalFieldName) => {
    setPostalDefaultValues(getPostalDefaultValues(field));
    setActivePostalField(field);
  };

  const getPortDefaultValues = (field: PortFieldName): PortSearchFormValues => {
    const isOrigin = field.startsWith('origin');

    return {
      country: isOrigin ? (values.originCountry ?? undefined) : (values.destinationCountry ?? undefined),
      portCity: (isOrigin ? values.originCity : values.destinationCity) ?? undefined,
      portPostal: (isOrigin ? values.originPostalFrom : values.destinationPostalFrom) ?? undefined,
      portState: isOrigin ? (values.originState ?? null) : (values.destinationState ?? null),
      portType: null,
    };
  };

  const openPortSearchModal = (field: PortFieldName) => {
    setPortDefaultValues(getPortDefaultValues(field));
    setActivePortField(field);
  };

  const handlePortSelectionChange = (port: PortRow) => {
    if (!activePortField) {
      return;
    }

    form.setFieldValue(activePortField, {
      id: port.portKey,
      label: port.name ?? String(port.portKey),
    });
    setActivePortField(null);
  };

  const handlePostalSelectionChange = (selection: Postals) => {
    if (!activePostalField) {
      return;
    }

    form.setFieldValue(activePostalField, selection[0]?.postalCode ?? '');
    setActivePostalField(null);
  };

  const onLaneSelectionChange = (lane: LaneRow) => {
    form.setFieldValue('laneKey', {
      id: lane.laneKey,
      label: lane.laneCode || String(lane.laneKey),
    });
    setIsLanesModalOpened(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <form.Field name="carrier">
        {(field) => (
          <CarrierField
            value={field.state.value ?? null}
            clearable
            defaultFilters={{ customerKey }}
            onSelectionChange={(carrier) => field.handleChange(carrier ?? undefined)}
            label="Transportation Provider"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="accessorialCode">
        {(field) => (
          <SelectField
            value={
              field.state.value ? (accessorialCodes?.map[field.state.value.id] ?? field.state.value) : field.state.value
            }
            clearable
            options={accessorialCodes?.list ?? []}
            onInputChange={updateAccessorialCodeFilters}
            onSelectionChange={(accessorial) =>
              field.handleChange(accessorial ? { id: String(accessorial.id), label: accessorial.label } : undefined)
            }
            label="Accessorial Name"
            placeholder="Select"
            size="sm"
            isLoading={accessorialCodesLoading}
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="accessorialKey">
        {(field) => (
          <InputField
            value={field.state.value ?? ''}
            clearable
            onChange={(e) => field.handleChange(e.target.value)}
            label="Accessorial Key"
            placeholder="Enter"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="originLocationKey">
        {(field) => (
          <InputField
            className={styles.field}
            label="Location From"
            clearable
            value={field.state.value?.label ?? ''}
            readOnly
            placeholder="Select"
            size="sm"
            errorMessage={getError(field)}
            onChange={() => field.handleChange(undefined)}
            onClick={() => setActiveLocationField('originLocationKey')}
          />
        )}
      </form.Field>
      <form.Field name="shipType">
        {(field) => (
          <ShipTypeField
            value={field.state.value ?? null}
            clearable
            onSelectionChange={(shipType) => field.handleChange(shipType ?? undefined)}
            label="Ship Type"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="isDangerousGoods">
        {(field) => (
          <SelectField
            value={field.state.value}
            clearable
            options={[...DANGEROUS_GOODS_OPTIONS]}
            onSelectionChange={(option) =>
              field.handleChange(option ? { id: option.id, label: option.label } : undefined)
            }
            label="Dangerous Goods"
            placeholder="Select"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="service">
        {(field) => (
          <CarrierServiceField
            value={field.state.value ?? null}
            clearable
            onSelectionChange={(carrierService) => field.handleChange(carrierService ?? undefined)}
            label="Service"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="branch">
        {(field) => (
          <BranchField
            value={field.state.value ?? null}
            clearable
            defaultFilters={{ customerKey }}
            onSelectionChange={(branch) => field.handleChange(branch ?? undefined)}
            label="Branch"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="equipmentType">
        {(field) => (
          <EquipmentTypeField
            value={field.state.value ?? null}
            clearable
            onSelectionChange={(equipmentType) => field.handleChange(equipmentType ?? undefined)}
            label="Equipment Type"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="pricingType">
        {(field) => (
          <PricingTypeField
            value={field.state.value ?? null}
            clearable
            onSelectionChange={(pricingType) => field.handleChange(pricingType ?? undefined)}
            label="Pricing Type"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="destinationLocationKey">
        {(field) => (
          <InputField
            className={styles.field}
            label="Location To"
            clearable
            value={field.state.value?.label ?? ''}
            readOnly
            placeholder="Select"
            size="sm"
            errorMessage={getError(field)}
            onChange={() => field.handleChange(undefined)}
            onClick={() => setActiveLocationField('destinationLocationKey')}
          />
        )}
      </form.Field>
      <form.Field name="directInterline">
        {(field) => (
          <DirectInterlineField
            value={field.state.value ?? null}
            clearable
            onSelectionChange={(directInterline) => field.handleChange(directInterline ?? undefined)}
            label="Direct/InterLine"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="currency">
        {(field) => (
          <CurrencyField
            value={field.state.value ?? null}
            clearable
            onSelectionChange={(currency) => field.handleChange(currency ?? undefined)}
            label="Currency"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="startEffectiveFromDate">
        {(field) => (
          <DatePickerField
            value={field.state.value ?? null}
            maxValue={rangeFromMaxValue}
            onChange={(date) => field.handleChange(date ?? undefined)}
            label="Effective Date Range"
            size="sm"
            timeZone={timeZone}
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="endEffectiveToDate">
        {(field) => (
          <DatePickerField
            value={field.state.value ?? null}
            minValue={rangeToMinValue}
            includeEndOfDay
            onChange={(date) => field.handleChange(date ?? undefined)}
            label="Expiration Date Range"
            size="sm"
            timeZone={timeZone}
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="originCountry">
        {(field) => (
          <CountryField
            value={field.state.value ?? null}
            clearable
            onSelectionChange={(country) => {
              field.handleChange(country ?? undefined);
              form.setFieldValue('originState', null);
            }}
            label="Search by Origin Country"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="originState">
        {(field) => (
          <StateField
            key={originCountryCode ?? 'no-country'}
            countryCode={originCountryCode}
            value={field.state.value ?? null}
            clearable
            onSelectionChange={(state) => field.handleChange(state ?? undefined)}
            label="Search by Origin State"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="originCity">
        {(field) => (
          <InputField
            value={field.state.value ?? ''}
            clearable
            onChange={(e) => field.handleChange(e.target.value)}
            label="Search by Origin City"
            placeholder="Enter"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="originPostalFrom">
        {(field) => (
          <InputField
            className={styles.field}
            label="Search by Origin Postal From"
            clearable
            value={field.state.value ?? ''}
            readOnly
            placeholder="Select"
            size="sm"
            errorMessage={getError(field)}
            onChange={() => field.handleChange(undefined)}
            onClick={() => openPostalSearchModal('originPostalFrom')}
          />
        )}
      </form.Field>
      <form.Field name="originPostalTo">
        {(field) => (
          <InputField
            className={styles.field}
            label="Search by Origin Postal To"
            clearable
            value={field.state.value ?? ''}
            readOnly
            placeholder="Select"
            size="sm"
            errorMessage={getError(field)}
            onChange={() => field.handleChange(undefined)}
            onClick={() => openPostalSearchModal('originPostalTo')}
          />
        )}
      </form.Field>
      <form.Field name="originPortKey">
        {(field) => (
          <InputField
            className={styles.field}
            label="Search by Origin Port"
            clearable
            value={field.state.value?.label ?? ''}
            readOnly
            placeholder="Select"
            size="sm"
            errorMessage={getError(field)}
            onChange={() => field.handleChange(undefined)}
            onClick={() => openPortSearchModal('originPortKey')}
          />
        )}
      </form.Field>
      <form.Field name="destinationCountry">
        {(field) => (
          <CountryField
            value={field.state.value ?? null}
            clearable
            onSelectionChange={(country) => {
              field.handleChange(country ?? undefined);
              form.setFieldValue('destinationState', null);
            }}
            label="Search by Destination Country"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="destinationState">
        {(field) => (
          <StateField
            key={destinationCountryCode ?? 'no-destination-country'}
            countryCode={destinationCountryCode}
            value={field.state.value ?? null}
            clearable
            onSelectionChange={(state) => field.handleChange(state ?? undefined)}
            label="Search by Destination State"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="destinationCity">
        {(field) => (
          <InputField
            value={field.state.value ?? ''}
            clearable
            onChange={(e) => field.handleChange(e.target.value)}
            label="Search by Destination City"
            placeholder="Enter"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="destinationPostalFrom">
        {(field) => (
          <InputField
            className={styles.field}
            label="Search by Destination Postal From"
            clearable
            value={field.state.value ?? ''}
            readOnly
            placeholder="Select"
            size="sm"
            errorMessage={getError(field)}
            onChange={() => field.handleChange(undefined)}
            onClick={() => openPostalSearchModal('destinationPostalFrom')}
          />
        )}
      </form.Field>
      <form.Field name="destinationPostalTo">
        {(field) => (
          <InputField
            className={styles.field}
            label="Search by Destination Postal To"
            clearable
            value={field.state.value ?? ''}
            readOnly
            placeholder="Select"
            size="sm"
            errorMessage={getError(field)}
            onChange={() => field.handleChange(undefined)}
            onClick={() => openPostalSearchModal('destinationPostalTo')}
          />
        )}
      </form.Field>
      <form.Field name="destinationPortKey">
        {(field) => (
          <InputField
            className={styles.field}
            label="Search by Destination Port"
            clearable
            value={field.state.value?.label ?? ''}
            readOnly
            placeholder="Select"
            size="sm"
            errorMessage={getError(field)}
            onChange={() => field.handleChange(undefined)}
            onClick={() => openPortSearchModal('destinationPortKey')}
          />
        )}
      </form.Field>
      <form.Field name="effectiveFromDate">
        {(field) => (
          <DatePickerField
            value={field.state.value}
            maxValue={effectiveFromMaxValue}
            onChange={(date) => {
              field.handleChange(date ?? '');
            }}
            label="Effective From"
            size="sm"
            timeZone={timeZone}
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="effectiveToDate">
        {(field) => (
          <DatePickerField
            value={field.state.value}
            minValue={effectiveToMinValue}
            includeEndOfDay
            onChange={(date) => field.handleChange(date ?? undefined)}
            label="Effective To"
            size="sm"
            timeZone={timeZone}
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>
      <form.Field name="laneKey">
        {(field) => (
          <InputField
            className={styles.field}
            label="Lane ID"
            clearable
            value={field.state.value?.label ?? ''}
            readOnly
            placeholder="Select"
            size="sm"
            onChange={() => field.handleChange(undefined)}
            onClick={() => setIsLanesModalOpened(true)}
          />
        )}
      </form.Field>
      <form.Subscribe
        selector={(state) => {
          return [state.canSubmit, state.isSubmitting, state.isPristine];
        }}
      >
        {([_canSubmit]) => (
          <button type="submit" style={{ display: 'none' }}>
            Hidden Submit
          </button>
        )}
      </form.Subscribe>

      <LocationSearchModal
        isOpen={activeLocationField !== null}
        setIsOpen={(open) => !open && setActiveLocationField(null)}
        onSelectionChange={handleLocationSelectionChange}
      />
      <PostalSearchModal
        defaultValues={postalDefaultValues}
        isOpen={activePostalField !== null}
        setIsOpen={(open) => !open && setActivePostalField(null)}
        onSelectionChange={handlePostalSelectionChange}
      />
      <PortSearchModal
        defaultValues={portDefaultValues}
        isOpen={activePortField !== null}
        setIsOpen={(open) => !open && setActivePortField(null)}
        onSelectionChange={handlePortSelectionChange}
      />
      <LaneSearchModal
        isOpen={isLanesModalOpened}
        setIsOpen={setIsLanesModalOpened}
        onSelectionChange={onLaneSelectionChange}
      />
    </form>
  );
};
